import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import CompetitionProgress from './index';

// Helper to create a date string offset from now
const getDateOffset = (days, hours = 0, minutes = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(date.getHours() + hours);
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

// Mock session data with timeBlocks for time-based progress
const createSessionsWithTimes = (count, options = {}) => {
  const {
    startOffset = 0, // days offset for first session start
    sessionDuration = 3, // hours per session
    gapBetweenSessions = 1 // hours between sessions
  } = options;

  return Array.from({ length: count }, (_, i) => {
    const sessionStartHours = i * (sessionDuration + gapBetweenSessions);
    const sessionEndHours = sessionStartHours + sessionDuration;

    return {
      sessionNumber: i + 1,
      gender: i % 2 === 0 ? 'F' : 'M',
      startDate: getDateOffset(startOffset, sessionStartHours),
      timeBlocks: {
        competitionStart: getDateOffset(startOffset, sessionStartHours),
        competitionEnd: getDateOffset(startOffset, sessionEndHours)
      }
    };
  });
};

// Mock gymnast data with scores
const createGymnasts = (sessionCount, athletesPerSession, completionPercentage) => {
  const gymnasts = [];
  const apparatusCount = 4; // Women's apparatus count

  for (let s = 1; s <= sessionCount; s++) {
    for (let a = 0; a < athletesPerSession; a++) {
      const scores = {};
      const totalScores = apparatusCount;
      const scoresToFill = Math.floor(totalScores * completionPercentage);

      for (let e = 1; e <= apparatusCount; e++) {
        if (e <= scoresToFill) {
          scores[`E${e}`] = { final: 12.5 + Math.random() * 2 };
        }
      }

      gymnasts.push({
        id: `${s}-${a}`,
        session: s,
        scratched: false,
        scores
      });
    }
  }

  return gymnasts;
};

const StateDemo = ({ title, subtitle, sessions, gymnasts, startDate, endDate, size }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      bgcolor: 'background.default',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      minWidth: 120
    }}
  >
    <CompetitionProgress
      sessions={sessions}
      gymnasts={gymnasts}
      gender="F"
      startDate={startDate}
      endDate={endDate}
      size={size}
    />
    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, textAlign: 'center' }}>
      {title}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
      {subtitle}
    </Typography>
  </Paper>
);

const CompetitionProgressDemo = () => {
  // Sessions for countdown demos (future dates, no time progress)
  const futureSessions = createSessionsWithTimes(4, { startOffset: 14 });
  const soonSessions = createSessionsWithTimes(4, { startOffset: 3 });
  const todaySessions = createSessionsWithTimes(4, { startOffset: 0, sessionDuration: 24 }); // starts in ~12 hours

  // Sessions with realistic time-based progress
  // Session 1: started 1.5 hours ago, ends in 1.5 hours (50% through time)
  const inProgressSessions = [
    {
      sessionNumber: 1,
      gender: 'F',
      timeBlocks: {
        competitionStart: getDateOffset(0, -1, -30), // 1.5 hours ago
        competitionEnd: getDateOffset(0, 1, 30) // 1.5 hours from now
      }
    },
    {
      sessionNumber: 2,
      gender: 'M',
      timeBlocks: {
        competitionStart: getDateOffset(0, 2), // 2 hours from now
        competitionEnd: getDateOffset(0, 5) // 5 hours from now
      }
    },
    {
      sessionNumber: 3,
      gender: 'F',
      timeBlocks: {
        competitionStart: getDateOffset(0, 6),
        competitionEnd: getDateOffset(0, 9)
      }
    },
    {
      sessionNumber: 4,
      gender: 'M',
      timeBlocks: {
        competitionStart: getDateOffset(1, 0), // tomorrow
        competitionEnd: getDateOffset(1, 3)
      }
    }
  ];

  // Sessions where session 1 is 75% through time
  const session1MostlyDone = [
    {
      sessionNumber: 1,
      gender: 'F',
      timeBlocks: {
        competitionStart: getDateOffset(0, -2, -15), // 2.25 hours ago
        competitionEnd: getDateOffset(0, 0, 45) // 45 min from now (75% through 3hr session)
      }
    },
    {
      sessionNumber: 2,
      gender: 'M',
      timeBlocks: {
        competitionStart: getDateOffset(0, 2),
        competitionEnd: getDateOffset(0, 5)
      }
    },
    {
      sessionNumber: 3,
      gender: 'F',
      timeBlocks: {
        competitionStart: getDateOffset(0, 6),
        competitionEnd: getDateOffset(0, 9)
      }
    },
    {
      sessionNumber: 4,
      gender: 'M',
      timeBlocks: {
        competitionStart: getDateOffset(1, 0),
        competitionEnd: getDateOffset(1, 3)
      }
    }
  ];

  // Sessions where session 1 ended, session 2 in progress
  const session2InProgress = [
    {
      sessionNumber: 1,
      gender: 'F',
      timeBlocks: {
        competitionStart: getDateOffset(0, -5),
        competitionEnd: getDateOffset(0, -2) // ended 2 hours ago
      }
    },
    {
      sessionNumber: 2,
      gender: 'M',
      timeBlocks: {
        competitionStart: getDateOffset(0, -1), // started 1 hour ago
        competitionEnd: getDateOffset(0, 2) // 2 hours from now (33% through)
      }
    },
    {
      sessionNumber: 3,
      gender: 'F',
      timeBlocks: {
        competitionStart: getDateOffset(0, 4),
        competitionEnd: getDateOffset(0, 7)
      }
    },
    {
      sessionNumber: 4,
      gender: 'M',
      timeBlocks: {
        competitionStart: getDateOffset(1, 0),
        competitionEnd: getDateOffset(1, 3)
      }
    }
  ];

  // Past sessions (all ended)
  const pastSessions = createSessionsWithTimes(4, { startOffset: -2 });

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        CompetitionProgress States Demo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Progress now uses time-based calculation during sessions for smoother updates.
        Each session contributes equally to overall progress.
      </Typography>

      {/* Large size demos */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Pre-Competition (Countdown)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <StateDemo
          title="14 Days Out"
          subtitle="Countdown"
          sessions={futureSessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(14)}
          endDate={getDateOffset(15)}
        />
        <StateDemo
          title="3 Days Out"
          subtitle="Countdown"
          sessions={soonSessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(3)}
          endDate={getDateOffset(4)}
        />
        <StateDemo
          title="< 1 Day"
          subtitle="Shows hours"
          sessions={todaySessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(0, 12)}
          endDate={getDateOffset(1, 12)}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        During Competition (Time-Based Progress)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Progress advances with time even before scores are entered. Shows max(time%, score%).
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <StateDemo
          title="Session 1: 50%"
          subtitle="Time-based (no scores)"
          sessions={inProgressSessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(2)}
        />
        <StateDemo
          title="Session 1: 75%"
          subtitle="Time-based (no scores)"
          sessions={session1MostlyDone}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(2)}
        />
        <StateDemo
          title="S1 done, S2: 33%"
          subtitle="Time-based progress"
          sessions={session2InProgress}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(2)}
        />
        <StateDemo
          title="With Scores"
          subtitle="50% scores entered"
          sessions={inProgressSessions}
          gymnasts={createGymnasts(4, 10, 0.5)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(2)}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Score-Based Progress
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <StateDemo
          title="Just Started"
          subtitle="0% complete"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
        />
        <StateDemo
          title="~25%"
          subtitle="1/4 sessions worth"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 0.25)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
        />
        <StateDemo
          title="~50%"
          subtitle="2/4 sessions worth"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 0.5)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
        />
        <StateDemo
          title="~75%"
          subtitle="3/4 sessions worth"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 0.75)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
        />
        <StateDemo
          title="Complete"
          subtitle="100% scores"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 1)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
        />
        <StateDemo
          title="Past End Date"
          subtitle="Date completed"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 0.8)}
          startDate={getDateOffset(-3)}
          endDate={getDateOffset(-1)}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Small size demos */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Small Size (Collapsed Header)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StateDemo
          title="14 Days Out"
          subtitle="Countdown"
          sessions={futureSessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(14)}
          endDate={getDateOffset(15)}
          size="small"
        />
        <StateDemo
          title="< 1 Day"
          subtitle="Shows hours"
          sessions={todaySessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(0, 12)}
          endDate={getDateOffset(1, 12)}
          size="small"
        />
        <StateDemo
          title="Time Progress"
          subtitle="Session 1: 50%"
          sessions={inProgressSessions}
          gymnasts={createGymnasts(4, 10, 0)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(2)}
          size="small"
        />
        <StateDemo
          title="~50%"
          subtitle="Score-based"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 0.5)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
          size="small"
        />
        <StateDemo
          title="Complete"
          subtitle="100% scores"
          sessions={pastSessions}
          gymnasts={createGymnasts(4, 10, 1)}
          startDate={getDateOffset(-1)}
          endDate={getDateOffset(1)}
          size="small"
        />
      </Box>
    </Box>
  );
};

export default CompetitionProgressDemo;
